import React, { useState, useEffect } from 'react';
import { Col, Row, Form, Card } from 'react-bootstrap';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import classNames from 'classnames';
import DatePicker from '../../components/base/DatePicker';
import Button from '../../components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

// CHARTS (păstrăm tot ce aveai)
import { TotalProducedChart } from '../../components/charts/machine-analitycs/TotalProducedChart';
import DynamicBarChart from '../../components/charts/machine-analitycs/DynamicBarChart';
import ActiveBreakBarChart from '../../components/charts/machine-analitycs/ActiveBreakBarChart';
import Dinamic2LineChart from '../../components/charts/machine-analitycs/Dinamic2LineChart';
import CurrentConsumedBarChart from '../../components/charts/machine-analitycs/CurrentConsumedBarChart';
import PvcConsumedBarChart from '../../components/charts/machine-analitycs/PvcConsumedBarChart';
import ShiftTotalActiveBreakTime from '../../components/charts/machine-analitycs/ShiftTotalActiveBreakTime';
import ShiftTotalProducedChart from '../../components/charts/machine-analitycs/ShiftTotalProducedChart';
import DynamicShiftBarChart from '../../components/charts/machine-analitycs/DynamicShiftBarChart';
import ShiftCurrentConsumedChart from '../../components/charts/machine-analitycs/ShiftCurrentConsumedChart';
import ShiftPvcConsumptionChart from '../../components/charts/machine-analitycs/ShiftPvcConsumptionChart';
import ActivityTimeline from '../../components/timelines/ActivityTimeline';
import Scrollbar from '../../components/base/Scrollbar';
import { Spinner } from 'react-bootstrap';
import { API, INFLUX_API } from '../../config/api';

dayjs.extend(utc);

// ==== HELPERS ====

const formatLabel = key =>
  key
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const convertData = (data, unit) => {
  const conversionFactors = {
    seconds: 1 / 3600, // sec -> ore
    meters: 1 / 1000, // m -> km
    kg: 1 / 1000 // kg -> T
  };

  const conversionLabel = {
    seconds: 'hours',
    meters: 'km',
    kg: 'T'
  };

  const factor = conversionFactors[unit] || 1;
  const newUnit = conversionLabel[unit] || unit;

  const convertedData = Object.entries(data || {}).reduce(
    (acc, [machine, values]) => {
      acc[machine] = (values || []).map(([time, value]) => [
        time,
        value * factor
      ]);
      return acc;
    },
    {}
  );

  return { convertedData, newUnit };
};

const MachineAnalytics = () => {
  // ==== STATE ====
  const [machines, setMachines] = useState([]); // full config from /machines-config
  const [machinesLoading, setMachinesLoading] = useState(true);

  // mașini selectate (filtru global) – varianta C: dropdown + sidebar legate de același state
  const [selectedMachines, setSelectedMachines] = useState([]);

  // metrici selectate pe fiecare mașină
  // shape: { [machineName]: { [metricKey]: boolean } }
  const [selectedMetrics, setSelectedMetrics] = useState({});

  // shift comparisons
  const [shiftComparisons, setShiftComparisons] = useState({
    totalProduced: false,
    totalProducedDiameter: false,
    currentConsumed: false,
    pvcConsumed: false,
    activeBreakTime: false
  });

  // per-machine timeline report toggle: { machineName: true/false }
  const [timelineEnabled, setTimelineEnabled] = useState({});

  // interval de timp (în stil DeepDive)
  const [fromDate, setFromDate] = useState(dayjs().subtract(12, 'hour'));
  const [toDate, setToDate] = useState(dayjs());

  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // timeline reports state — grouped by machine: { machineName: reports[] }
  const [timelineByMachine, setTimelineByMachine] = useState({});
  const [timelineLoading, setTimelineLoading] = useState(false);

  // ==== LOAD MACHINES CONFIG (ca în DeepDive) ====
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const res = await axios.get(
          `${INFLUX_API}/api/machines-config`
        );
        const machinesArr = res.data.machines || [];
        setMachines(machinesArr);

        // mașini care au machineReport definit
        const withReports = machinesArr.filter(
          m => Array.isArray(m.machineReport) && m.machineReport.length > 0
        );

        const defaultSelected = withReports.map(m => m.name);
        setSelectedMachines(defaultSelected);

        // inițializăm selectedMetrics cu false (nimic bifat)
        const metricsInit = {};
        withReports.forEach(m => {
          metricsInit[m.name] = {};
          m.machineReport.forEach(key => {
            metricsInit[m.name][key] = false;
          });
        });
        setSelectedMetrics(metricsInit);
      } catch (err) {
        console.error('❌ Error loading machines config:', err);
        setError('Error loading machines configuration.');
      } finally {
        setMachinesLoading(false);
      }
    };

    fetchMachines();
  }, []);

  const toggleSelectAllShiftReports = checked => {
    setShiftComparisons({
      totalProduced: checked,
      totalProducedDiameter: checked,
      currentConsumed: checked,
      pvcConsumed: checked,
      activeBreakTime: checked
    });
  };

  const toggleSelectAllMachines = checked => {
    if (checked) {
      // selectăm toate mașinile cu report
      const all = machinesWithReports.map(m => m.name);
      setSelectedMachines(all);
    } else {
      // deselectăm tot
      setSelectedMachines([]);
    }
  };

  // ==== HANDLERS ====

  const handleMachineToggleSidebar = (machineName, checked) => {
    setSelectedMachines(prev => {
      if (checked) {
        if (prev.includes(machineName)) return prev;
        return [...prev, machineName];
      }
      return prev.filter(m => m !== machineName);
    });
  };

  const handleMachineSelectHeader = e => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map(o => o.value);
    setSelectedMachines(values);
  };

  const handleMetricChange = (machine, key, checked) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [machine]: {
        ...(prev[machine] || {}),
        [key]: checked
      }
    }));
  };

  const handleSelectAllMetrics = (machine, allKeys, checked) => {
    setSelectedMetrics(prev => {
      const current = prev[machine] || {};
      const updated = { ...current };
      allKeys.forEach(k => {
        updated[k] = checked;
      });
      return { ...prev, [machine]: updated };
    });
  };

  const handleShiftComparisonChange = (key, checked) => {
    setShiftComparisons(prev => ({ ...prev, [key]: checked }));
  };

  const handleSubmit = async () => {
    setError('');

    if (!fromDate || !toDate) {
      setError('Please select start and end date/time.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      setError('Start must be before end.');
      return;
    }

    // verificăm dacă avem metrici selectate pe cel puțin o mașină
    const isMetricSelected = Object.values(selectedMetrics).some(metrics =>
      Object.values(metrics).some(v => v)
    );

    const isShiftSelected = Object.values(shiftComparisons).some(v => v);
    const isTimelineSelected = Object.values(timelineEnabled).some(v => v);

    if (!isMetricSelected && !isShiftSelected && !isTimelineSelected) {
      setError('Select at least one metric, shift comparison, or timeline report.');
      return;
    }

    // construim obiectul metrics { machineName: [metricKey, ...] }
    const metrics = Object.entries(selectedMetrics).reduce(
      (acc, [machine, metricsObj]) => {
        // ignorăm mașinile care nu sunt în selectedMachines (filtru global)
        if (!selectedMachines.includes(machine)) return acc;

        const selected = Object.keys(metricsObj).filter(key => metricsObj[key]);
        if (selected.length > 0) acc[machine] = selected;
        return acc;
      },
      {}
    );

    // dacă nu mai rămâne nimic după filtrul de mașini
    const hasAnyMetric = Object.keys(metrics).length > 0;
    if (!hasAnyMetric && !isShiftSelected && !isTimelineSelected) {
      setError('Nothing selected for the current machine filter.');
      return;
    }

    // conversie în UTC (ca în DeepDive)
    const startDateTime = fromDate
      .local()
      .utc()
      .second(0)
      .millisecond(0)
      .format();
    const endDateTime = toDate.local().utc().second(0).millisecond(0).format();

    console.log('Requesting data from', startDateTime, 'to', endDateTime);
    const requestData = {
      metrics,
      shiftComparisons,
      startDateTime,
      endDateTime
    };

    // only fetch metrics/shift data if any are selected
    if (hasAnyMetric || isShiftSelected) {
      try {
        setLoading(true);
        setChartData({});

        const response = await axios.post(
          `${INFLUX_API}/api/influx/metrics`,
          requestData,
          {
            headers: { 'Content-Type': 'application/json' }
          }
        );

        const receivedData = response.data;

        setChartData(receivedData || {});
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error fetching metrics data. See console.');
      } finally {
        setLoading(false);
      }
    }

    // fetch timeline reports only for machines that have it checked
    const machinesWithTimeline = selectedMachines.filter(
      m => timelineEnabled[m]
    );
    if (machinesWithTimeline.length > 0) {
      setTimelineLoading(true);
      setTimelineByMachine({});
      try {
        const result = {};
        for (const machineName of machinesWithTimeline) {
          const res = await axios.get(
            `${API.BACKEND}/api/v1/machines/reports`,
            {
              params: {
                name: machineName,
                dateFrom: fromDate.toISOString(),
                dateTo: toDate.toISOString()
              }
            }
          );

          console.log(res);
          let reports = (res.data?.data || []).map((r, i) => ({
            ...r,
            id: `${machineName}-${i}-${Date.now()}`
          }));
          reports.sort((a, b) => {
            const da = new Date(
              `${a.oppositeContent?.date} ${a.oppositeContent?.time}`
            );
            const db = new Date(
              `${b.oppositeContent?.date} ${b.oppositeContent?.time}`
            );
            return db - da;
          });
          // limit to 200 most recent to avoid rendering issues
          if (reports.length > 200) {
            reports = reports.slice(0, 200);
          }
          if (reports.length > 0) {
            result[machineName] = reports;
          }
        }
        setTimelineByMachine(result);
      } catch (err) {
        console.error('Error fetching timeline reports:', err);
      } finally {
        setTimelineLoading(false);
      }
    } else {
      setTimelineByMachine({});
    }
  };

  // ==== DERIVED ====

  const machinesWithReports = machines.filter(
    m => Array.isArray(m.machineReport) && m.machineReport.length > 0
  );

  const filteredMachines = machinesWithReports.filter(m =>
    selectedMachines.includes(m.name)
  );

  // ==== SPINNER / EMPTY STATES ====
  if (machinesLoading) {
    return (
      <div style={styles.spinnerContainer}>
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  return (
    <div className="p-3">
      <h4 className="mb-3">Analytics</h4>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* =============== SELECȚIE MAȘINI & METRICI =============== */}
      <Row className="g-2 mb-3">
        {/* SIDEBAR: Machines (varianta C) */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
              <span>Machines</span>

              <Form.Check
                type="switch"
                id="select-all-machines"
                label="All"
                checked={selectedMachines.length === machinesWithReports.length}
                onChange={e => toggleSelectAllMachines(e.target.checked)}
              />
            </Card.Header>
            <Card.Body
              style={{
                maxHeight: '45vh',
                overflowY: 'auto',
                paddingTop: '8px',
                paddingBottom: '8px'
              }}
            >
              {machinesWithReports.map(m => (
                <div
                  key={m.name}
                  className={classNames(
                    'd-flex justify-content-between align-items-center py-1 px-2 mb-1 rounded-2',
                    {
                      'bg-200': selectedMachines.includes(m.name)
                    }
                  )}
                >
                  <span className="fw-semibold text-uppercase">{m.name}</span>
                  <Form.Check
                    type="switch"
                    id={`machine-${m.name}`}
                    checked={selectedMachines.includes(m.name)}
                    onChange={e =>
                      handleMachineToggleSidebar(m.name, e.target.checked)
                    }
                  />
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* METRICS PER MACHINE */}
        <Col md={9}>
          <Card className="h-100">
            <Card.Header className="fw-bold d-flex justify-content-between">
              Metrics per Machine
              <span className="text-muted small">
                Select what report to display for each machine
              </span>
            </Card.Header>
            <Card.Body
              style={{
                maxHeight: '45vh',
                overflowY: 'auto',
                paddingTop: '10px',
                paddingBottom: '10px'
              }}
            >
              {filteredMachines.length === 0 && (
                <div className="text-muted fst-italic">
                  No machine selected from filter
                </div>
              )}

              {filteredMachines.map(machine => {
                const metricsKeys = machine.machineReport || [];
                const allSelected =
                  metricsKeys.length > 0 &&
                  metricsKeys.every(k => selectedMetrics[machine.name]?.[k]);

                return (
                  <div key={machine.name} className="mb-3 border-bottom pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-bold text-uppercase">
                        {machine.name}
                        <span className="text-muted ms-2 small">
                          (
                          {
                            Object.values(
                              selectedMetrics[machine.name] || {}
                            ).filter(v => v).length
                          }{' '}
                          selected)
                        </span>
                      </div>
                      <Form.Check
                        type="switch"
                        id={`select-all-${machine.name}`}
                        label={allSelected ? 'Clear' : 'All'}
                        checked={allSelected}
                        onChange={e =>
                          handleSelectAllMetrics(
                            machine.name,
                            metricsKeys,
                            e.target.checked
                          )
                        }
                      />
                    </div>
                    <Row className="g-1">
                      {metricsKeys.map(key => (
                        <Col md={4} key={key}>
                          <Form.Check
                            type="checkbox"
                            id={`${machine.name}-${key}`}
                            label={formatLabel(key)}
                            checked={
                              selectedMetrics[machine.name]?.[key] || false
                            }
                            onChange={e =>
                              handleMetricChange(
                                machine.name,
                                key,
                                e.target.checked
                              )
                            }
                          />
                        </Col>
                      ))}
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id={`${machine.name}-timeline-report`}
                          label="Timeline Report"
                          className="fw-semibold text-primary"
                          checked={timelineEnabled[machine.name] || false}
                          onChange={e =>
                            setTimelineEnabled(prev => ({
                              ...prev,
                              [machine.name]: e.target.checked
                            }))
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* =============== SHIFT / TIMELINE REPORTS (ce aveai la "timeline") =============== */}
      <Row className="g-2 mb-3">
        <Col md={12}>
          <Card>
            <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
              <span>Shift & Timeline Reports</span>

              <Form.Check
                type="switch"
                id="select-all-shift-reports"
                label="All"
                checked={Object.values(shiftComparisons).every(v => v)}
                onChange={e => toggleSelectAllShiftReports(e.target.checked)}
              />
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Check type="checkbox" id="SHIFT_TOTAL_PRODUCED">
                    <Form.Check.Input
                      type="checkbox"
                      checked={shiftComparisons.totalProduced}
                      onChange={e =>
                        handleShiftComparisonChange(
                          'totalProduced',
                          e.target.checked
                        )
                      }
                    />
                    <Form.Check.Label className="text-body fs-8">
                      Shift Comparison Total Produced
                    </Form.Check.Label>
                  </Form.Check>

                  <Form.Check
                    type="checkbox"
                    id="SHIFT_TOTAL_PRODUCED_ON_DIAMETER"
                  >
                    <Form.Check.Input
                      type="checkbox"
                      checked={shiftComparisons.totalProducedDiameter}
                      onChange={e =>
                        handleShiftComparisonChange(
                          'totalProducedDiameter',
                          e.target.checked
                        )
                      }
                    />
                    <Form.Check.Label className="text-body fs-8">
                      Shift Comparison Total Produced On Diameter
                    </Form.Check.Label>
                  </Form.Check>
                </Col>

                <Col md={4}>
                  <Form.Check type="checkbox" id="SHIFT_CURRENT_CONSUMED">
                    <Form.Check.Input
                      type="checkbox"
                      checked={shiftComparisons.currentConsumed}
                      onChange={e =>
                        handleShiftComparisonChange(
                          'currentConsumed',
                          e.target.checked
                        )
                      }
                    />
                    <Form.Check.Label className="text-body fs-8">
                      Shift Comparison Current Consumed
                    </Form.Check.Label>
                  </Form.Check>

                  <Form.Check type="checkbox" id="SHIFT_PVC_CONSUMED">
                    <Form.Check.Input
                      type="checkbox"
                      checked={shiftComparisons.pvcConsumed}
                      onChange={e =>
                        handleShiftComparisonChange(
                          'pvcConsumed',
                          e.target.checked
                        )
                      }
                    />
                    <Form.Check.Label className="text-body fs-8">
                      Shift Comparison PVC Consumed
                    </Form.Check.Label>
                  </Form.Check>
                </Col>

                <Col md={4}>
                  <Form.Check type="checkbox" id="SHIFT_ACTIVE_BREAK_TIME">
                    <Form.Check.Input
                      type="checkbox"
                      checked={shiftComparisons.activeBreakTime}
                      onChange={e =>
                        handleShiftComparisonChange(
                          'activeBreakTime',
                          e.target.checked
                        )
                      }
                    />
                    <Form.Check.Label className="text-body fs-8">
                      Shift Comparison Active &amp; Break times
                    </Form.Check.Label>
                  </Form.Check>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              {/* START DATE */}
              <Col md={2}>
                <Form.Label>Start Date</Form.Label>
                <DatePicker
                  value={fromDate.toDate()}
                  onChange={([d]) =>
                    setFromDate(prev =>
                      prev
                        .set('year', dayjs(d).year())
                        .set('month', dayjs(d).month())
                        .set('date', dayjs(d).date())
                    )
                  }
                />
              </Col>

              {/* START TIME */}
              <Col md={2}>
                <Form.Label>Start Time</Form.Label>
                <DatePicker
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i'
                  }}
                  value={fromDate.format('HH:mm')}
                  hideIcon
                  onChange={([t]) =>
                    setFromDate(prev =>
                      prev
                        .set('hour', dayjs(t).hour())
                        .set('minute', dayjs(t).minute())
                    )
                  }
                />
              </Col>

              {/* END DATE */}
              <Col md={2}>
                <Form.Label>End Date</Form.Label>
                <DatePicker
                  value={toDate.toDate()}
                  onChange={([d]) =>
                    setToDate(prev =>
                      prev
                        .set('year', dayjs(d).year())
                        .set('month', dayjs(d).month())
                        .set('date', dayjs(d).date())
                    )
                  }
                />
              </Col>

              {/* END TIME */}
              <Col md={2}>
                <Form.Label>End Time</Form.Label>
                <DatePicker
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i'
                  }}
                  value={toDate.format('HH:mm')}
                  hideIcon
                  onChange={([t]) =>
                    setToDate(prev =>
                      prev
                        .set('hour', dayjs(t).hour())
                        .set('minute', dayjs(t).minute())
                    )
                  }
                />
              </Col>

              {/* LOAD BUTTON */}
              <Col md="auto" className="ms-auto">
                <Button
                  variant="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faCheck} />}
                  className="px-4"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Loading' : 'Load'}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Row>

      {/* =============== CHARTS (păstrăm comportamentul existent) =============== */}
      <Row className="gx-0 gx-sm-5 gy-4 mt-1 mb-4 border-bottom border-300">
        {chartData.total_produced && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.total_produced
              ).every(values => values.length <= 1);

              const { convertedData, newUnit } = convertData(
                chartData.total_produced,
                'meters'
              );

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={convertedData}
                  title="Total Produced - All Machines"
                  unit={newUnit}
                />
              ) : (
                <TotalProducedChart
                  data={convertedData}
                  title="Total Produced - All Machines"
                  unit={newUnit}
                />
              );
            })()}
          </Col>
        )}

        {chartData.TOTAL_UNWINDING && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.TOTAL_UNWINDING
              ).every(values => values.length <= 1);

              const { convertedData, newUnit } = convertData(
                chartData.TOTAL_UNWINDING,
                'meters'
              );

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={convertedData}
                  title="Total UNWINDING - All Machines"
                  unit={newUnit}
                />
              ) : (
                <TotalProducedChart
                  data={convertedData}
                  title="Total UNWINDING - All Machines"
                  unit={newUnit}
                />
              );
            })()}
          </Col>
        )}

        {chartData.TOTAL_TIME && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.TOTAL_TIME
              ).every(values => values.length <= 1);

              const { convertedData, newUnit } = convertData(
                chartData.TOTAL_TIME,
                'seconds'
              );

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={convertedData}
                  title="Total Time - All Machines"
                  unit={newUnit}
                />
              ) : (
                <TotalProducedChart
                  data={convertedData}
                  title="Total Time - All Machines"
                  unit={newUnit}
                />
              );
            })()}
          </Col>
        )}

        {(chartData.ACTIVE_TIME || chartData.BREAK_TIME) && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const { convertedData: activeConvertedData } = convertData(
                chartData.ACTIVE_TIME,
                'seconds'
              );

              const { convertedData: breakConvertedData } = convertData(
                chartData.BREAK_TIME,
                'seconds'
              );

              return (
                <ActiveBreakBarChart
                  activeData={activeConvertedData}
                  breakData={breakConvertedData}
                  title="ACTIVE BREAK TIME"
                />
              );
            })()}
          </Col>
        )}

        {chartData.TOTAL_CURRENT && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.TOTAL_CURRENT
              ).every(values => values.length <= 1);

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={chartData.TOTAL_CURRENT}
                  title="Total Current Consumed - All Machines"
                  unit="amps"
                />
              ) : (
                <TotalProducedChart
                  data={chartData.TOTAL_CURRENT}
                  title="Total Current Consumed - All Machines"
                  unit="amps"
                />
              );
            })()}
          </Col>
        )}

        {chartData.TOTAL_PVC && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.TOTAL_PVC
              ).every(values => values.length <= 1);

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={chartData.TOTAL_PVC}
                  title="Total PVC Consumed - All Machines"
                  unit="kg"
                />
              ) : (
                <TotalProducedChart
                  data={chartData.TOTAL_PVC}
                  title="Total PVC Consumed - All Machines"
                  unit="kg"
                />
              );
            })()}
          </Col>
        )}

        {chartData.TOTAL_COLOR && (
          <Col xs={12} sm={12} lg={6}>
            {(() => {
              const allMachinesHaveSingleEntry = Object.values(
                chartData.TOTAL_COLOR
              ).every(values => values.length <= 1);

              return allMachinesHaveSingleEntry ? (
                <DynamicBarChart
                  data={chartData.TOTAL_COLOR}
                  title="Total Color Consumed - All Machines"
                  unit="kg"
                />
              ) : (
                <TotalProducedChart
                  data={chartData.TOTAL_COLOR}
                  title="Total Color Consumed - All Machines"
                  unit="kg"
                />
              );
            })()}
          </Col>
        )}

        {(chartData.E75_EXTRUDER_ACTUAL_COLOR_YIELD ||
          chartData.E75_EXTRUDER_ACTUAL_YIELD_PLC ||
          chartData.E45_EXTRUDER_ACTUAL_YIELD_PLC ||
          chartData.E45_EXTRUDER_ACTUAL_COLOR_YIELD) && (
          <Col xs={12} sm={12} lg={6}>
            <PvcConsumedBarChart
              data={{
                E75_EXTRUDER_ACTUAL_COLOR_YIELD:
                  chartData.E75_EXTRUDER_ACTUAL_COLOR_YIELD,
                E75_EXTRUDER_ACTUAL_YIELD_PLC:
                  chartData.E75_EXTRUDER_ACTUAL_YIELD_PLC,
                E45_EXTRUDER_ACTUAL_YIELD_PLC:
                  chartData.E45_EXTRUDER_ACTUAL_YIELD_PLC,
                E45_EXTRUDER_ACTUAL_COLOR_YIELD:
                  chartData.E45_EXTRUDER_ACTUAL_COLOR_YIELD
              }}
              title="Extruders PVC & Color Consumption"
            />
          </Col>
        )}

        {(chartData.E45_MotorCurrentValue ||
          chartData.E75_MotorCurrentValue ||
          chartData.BOW_CURRENT ||
          chartData.CAPSTAN_CURRENT ||
          chartData.CURRENT_ACT ||
          chartData.ENTRY_CAT_CURRENT) && (
          <Col xs={12} sm={12} lg={6}>
            <CurrentConsumedBarChart
              data={{
                BOW_CURRENT: chartData.BOW_CURRENT,
                E75_MotorCurrentValue: chartData.E75_MotorCurrentValue,
                CAPSTAN_CURRENT: chartData.CAPSTAN_CURRENT,
                CURRENT_ACT: chartData.CURRENT_ACT,
                E45_MotorCurrentValue: chartData.E45_MotorCurrentValue,
                ENTRY_CAT_CURRENT: chartData.ENTRY_CAT_CURRENT
              }}
              title="Machine Component Current"
            />
          </Col>
        )}

        {chartData.activeBreakTime && (
          <Col xs={12} sm={12} lg={6}>
            <ShiftTotalActiveBreakTime
              shiftData={chartData.activeBreakTime}
              title="Total Active&Break Detailed View"
            />
          </Col>
        )}

        {chartData.totalProduced && (
          <Col xs={12} sm={12} lg={6}>
            <ShiftTotalProducedChart
              shiftData={chartData.totalProduced}
              title="Total produced on Shifts"
            />
          </Col>
        )}

        {chartData.currentConsumed && (
          <Col xs={12} sm={12} lg={6}>
            <ShiftCurrentConsumedChart
              shiftData={chartData.currentConsumed}
              title="Total Current Consumed On Shift"
            />
          </Col>
        )}

        {chartData.pvcConsumed && (
          <Col xs={12} sm={12} lg={6}>
            <ShiftPvcConsumptionChart
              shiftData={chartData.pvcConsumed}
              title="PVC CONSUMED L75 ON SHIFTS"
            />
          </Col>
        )}

        {chartData.totalProducedDiameter && (
          <Col xs={12}>
            <DynamicShiftBarChart data={chartData.totalProducedDiameter} />
          </Col>
        )}
      </Row>

      {/* =============== TIMELINE REPORTS PER MACHINE =============== */}
      {(timelineLoading ||
        Object.keys(timelineByMachine).length > 0 ||
        Object.values(timelineEnabled).some(v => v)) && (
        <Row className="mt-4 g-3">
          {timelineLoading ? (
            <Col xs={12}>
              <Card>
                <Card.Body className="d-flex justify-content-center align-items-center py-5">
                  <Spinner
                    animation="border"
                    variant="primary"
                    size="sm"
                    className="me-2"
                  />
                  <span>Loading timeline...</span>
                </Card.Body>
              </Card>
            </Col>
          ) : Object.keys(timelineByMachine).length === 0 ? (
            <Col xs={12}>
              <Card>
                <Card.Body className="text-center text-700 py-5">
                  No timeline reports found for the selected machines and date
                  range.
                </Card.Body>
              </Card>
            </Col>
          ) : (
            Object.entries(timelineByMachine).map(([machineName, reports]) => (
              <Col xs={12} lg={6} key={machineName}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      {machineName.toUpperCase()} — Timeline
                    </h5>
                    <span className="text-700 fs-9">
                      {reports.length} events
                    </span>
                  </Card.Header>
                  <Card.Body className="px-3 py-2">
                    <ActivityTimeline reports={reports} />
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
      )}
    </div>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  }
};

export default MachineAnalytics;
